/* eslint-disable ember/require-computed-property-dependencies */
import { tracked } from '@glimmer/tracking';
import Controller from '@ember/controller';
import EmberObject, { action, computed } from '@ember/object';
import { service } from '@ember/service';
import { isEmpty } from '@ember/utils';
import TransactionType from '@linc-technologies/linced-common/enums/transaction-type';

export default class LedgerItemSummaryController extends Controller {
  @service
  strings;

  @tracked
  cancelled = false;

  @computed('model.{transactions.[],apportionments.[]}')
  get data() {
    let data = EmberObject.create({});
    const reducer = (data, transaction) => {
      let lineItem = transaction.lineItem;
      if (isEmpty(lineItem)) {
        // No line item - either a Payment or a direct refund from account.
        return data;
      }
      let ledgerId = lineItem.get('ledger.id');
      if (isEmpty(ledgerId)) {
        // No Ledger to map this to - PIA?
        return data;
      }
      let ledgerItem = lineItem.get('ledgerItem');
      if (isEmpty(data[ledgerId])) {
        // We haven't seen this ledger yet; add it to the map.
        data.set(
          ledgerId,
          EmberObject.create({
            ledger: lineItem.get('ledger'),
            items: [],
            total: 0,
            tax: 0,
          }),
        );
      }

      if (isEmpty(ledgerItem.get('id'))) {
        // If there's no Ledger Item, create a dummy one (per Ledger) called "Other".
        ledgerItem = EmberObject.create({
          id: 0,
          name: 'Other',
        });
      }

      let itemId = ledgerItem.get('id');
      if (isEmpty(data.get(ledgerId).items[itemId])) {
        // We haven't seen this Ledger Item yet; add it to the map.
        data[ledgerId].items[itemId] = EmberObject.create({
          item: ledgerItem,
          total: 0,
          tax: 0,
          isTaxable: ledgerItem.get('isTaxable'),
        });
      }

      if (itemId === 0 && lineItem.get('taxPercentage') > 0) {
        // A line item with tax that's being bucketed under the dummy Ledger
        // Item.  Flag the dummy Ledger Item as taxable, in case this Ledger
        // doesn't have any "real" Ledger Items which are taxable.
        data[ledgerId].items[itemId].isTaxable = true;
      }

      // Update both ledger and ledger item totals.
      data[ledgerId].items[itemId].incrementProperty(
        'total',
        transaction.displayAmount,
      );
      data[ledgerId].items[itemId].incrementProperty(
        'tax',
        transaction.taxAmount,
      );

      data[ledgerId].incrementProperty('total', transaction.displayAmount);
      data[ledgerId].incrementProperty('tax', transaction.taxAmount);

      return data;
    };
    data = this.model.transactions.reduce(reducer, data);

    if (!isEmpty(this.model.apportionments)) {
      data = this.model.apportionments.reduce(reducer, data);
    }

    let pia = this.piaData;
    if (this.piaData.total) {
      data['pia'] = pia;
    }

    return data;
  }

  @computed('model.{transactions.[],apportionments.[]}')
  get piaData() {
    const reducer = (total, transaction) => {
      let amount = transaction.piaAmount;
      switch (transaction.type) {
        case TransactionType.REFUND:
          if (isEmpty(transaction.get('lineItem.id'))) {
            // Direct refunds from PIA need to use `amount`.
            amount = transaction.amount;
          }
          break;
        case TransactionType.TRANSFER:
          // Transfers to/from PIA need to use `amount`.
          amount = transaction.amount;
          break;
      }

      return total + amount;
    };

    let transactions = this.model.transactions.filter((transaction) => {
      if (
        transaction.type === TransactionType.TRANSFER &&
        !isEmpty(transaction.get('account.id'))
      ) {
        return true;
      }
      return isEmpty(transaction.get('lineItem.id'));
    });
    let total = transactions.reduce(reducer, 0);

    if (!isEmpty(this.model.apportionments)) {
      let apportionments = this.model.apportionments.filter(
        (item) => !item.line_item_id,
      );
      total = apportionments.reduce(reducer, total);
    }

    return EmberObject.create({
      ledger: {
        displayName: 'Payment in Advance',
      },
      items: [],
      total,
      tax: 0,
    });
  }

  @computed('data')
  get grandTotal() {
    let total = 0;
    Object.keys(this.data).forEach((ledgerId) => {
      total += this.data.get(ledgerId).total;
    });
    return total;
  }

  @computed('data')
  get taxTotal() {
    let total = 0;
    Object.keys(this.data).forEach((ledgerId) => {
      total += this.data.get(ledgerId).tax;
    });
    return total;
  }

  @tracked
  _reportTitle = null;

  @action
  set_reportTitle(value) {
    this._reportTitle = value;
  }

  @computed(
    'transactionType',
    'from',
    'to',
    'cancelled',
    'strings.financeLedgerOrItem',
    '_reportTitle',
  )
  get reportTitle() {
    if (this._reportTitle !== null) {
      return this._reportTitle;
    }
    let typeName = TransactionType.getLabel(parseInt(this.transactionType, 10));
    if (this.cancelled) {
      typeName = `Cancelled ${typeName}`;
    }
    if (isEmpty(typeName)) {
      typeName = 'All Transactions';
      if (this.cancelled) {
        typeName = 'All Cancelled Transactions';
      }
    }
    return `${this.strings.financeLedgerOrItem} Summary: ${typeName} (${this.from} - ${this.to})`;
  }
}
